// Credit Type Registry - Centralized configuration for all credit providers
// This replaces scattered if/else conditional logic throughout the codebase

export const BASE_CHAIN_ID = 8453; // Base chain ID
export const HYPER_EVM_CHAIN_ID = 999; // HyperEVM chain ID

// Credit Type Classification
export type CreditTypeClassification = "spend" | "borrow";

// Credit Provider Configuration Interface
export interface CreditProvider {
  id: CreditProviderId;
  storeKey: StoreCreditType;
  providerKey: ProviderKey;
  displayName: string;
  chainId: number;
  decimals: number;
  creditType: CreditTypeClassification;
  config: {
    // Borrow Mode
    borrowTokenAddress?: string;
    borrowVaultAddress?: string;

    // Spend Mode
    aTokenAddress?: string; // For Aave-type vaults
    vaultTokenAddress?: string; // For Morpho-type vaults
  };
  minDelegateAmount?: number;
  icon: string;
  canDCA: boolean;
}

// Centralized Credit Registry - Single source of truth
export const CREDIT_REGISTRY = {
  // Borrow Mode
  "aave-v3": {
    id: "aave-v3",
    storeKey: "aave",
    providerKey: "aave",
    displayName: "Aave",
    chainId: BASE_CHAIN_ID,
    decimals: 6,
    creditType: "borrow" as const,
    config: {
      borrowTokenAddress: "0x833589fcd6edb6e08f4c7c32d4f71b54bda02913",
    },
    minDelegateAmount: 1,
    icon: "AaveIcon",
    canDCA: false,
  },
  euler: {
    id: "euler",
    storeKey: "euler",
    providerKey: "euler",
    displayName: "Euler",
    chainId: BASE_CHAIN_ID,
    decimals: 18,
    creditType: "borrow" as const,
    config: {
      borrowVaultAddress: "0x0A1a3b5f2041F33522C4efc754a7D096f880eE16",
    },
    icon: "EulerIcon",
    canDCA: false,
  },
  hypurrFi: {
    id: "hypurrFi",
    storeKey: "hypurrFi",
    providerKey: "hypurrfi",
    displayName: "HypurrFi",
    chainId: HYPER_EVM_CHAIN_ID,
    decimals: 6,
    creditType: "borrow" as const,
    config: {
      borrowTokenAddress: "0xb8ce59fc3717ada4c02eadf9682a9e934f625ebb",
    },
    minDelegateAmount: 1,
    icon: "HypurrFiIcon",
    canDCA: false,
  },

  // Spend Mode
  "hypurrFi-withdraw": {
    id: "hypurrFi-withdraw",
    storeKey: "hypurrFiWithdraw",
    providerKey: "hypurrFi-withdraw",
    displayName: "HypurrFi",
    chainId: HYPER_EVM_CHAIN_ID,
    decimals: 6,
    creditType: "spend" as const,
    config: {
      aTokenAddress: "0x0000000000000000000000000000000000000000",
    },
    minDelegateAmount: 1,
    icon: "HypurrFiIcon",
    canDCA: true,
  },
  "felix-withdraw": {
    id: "felix-withdraw",
    storeKey: "felixWithdraw",
    providerKey: "felix-withdraw",
    displayName: "Felix",
    chainId: HYPER_EVM_CHAIN_ID,
    decimals: 6,
    creditType: "spend" as const,
    config: {
      vaultTokenAddress: "0x0000000000000000000000000000000000000000",
    },
    minDelegateAmount: 1,
    icon: "FelixIcon",
    canDCA: true,
  },
  "hyperBeat-withdraw": {
    id: "hyperBeat-withdraw",
    storeKey: "hyperBeatWithdraw",
    providerKey: "hyperBeat-withdraw",
    displayName: "HyperBeat",
    chainId: HYPER_EVM_CHAIN_ID,
    decimals: 6,
    creditType: "spend" as const,
    config: {
      vaultTokenAddress: "0x0000000000000000000000000000000000000000",
    },
    minDelegateAmount: 1,
    icon: "HyperBeatIcon",
    canDCA: true,
  },
  "hyperLend-withdraw": {
    id: "hyperLend-withdraw",
    storeKey: "hyperLendWithdraw",
    providerKey: "hyperLend-withdraw",
    displayName: "HyperLend",
    chainId: HYPER_EVM_CHAIN_ID,
    decimals: 6,
    creditType: "spend" as const,
    config: {
      aTokenAddress: "0x0000000000000000000000000000000000000000",
    },
    minDelegateAmount: 1,
    icon: "HyperLendIcon",
    canDCA: true,
  },
} as const;

// Type definitions
export type CreditProviderId = keyof typeof CREDIT_REGISTRY;
export type StoreCreditType =
  (typeof CREDIT_REGISTRY)[keyof typeof CREDIT_REGISTRY]["storeKey"];
export type ProviderKey =
  (typeof CREDIT_REGISTRY)[keyof typeof CREDIT_REGISTRY]["providerKey"];

// Helper Functions - Replace scattered conditional logic

/**
 * Get a specific credit provider by ID
 * @param id - Credit provider ID
 * @returns Credit provider configuration or null if not found
 */
export const getCreditProvider = (
  id: CreditProviderId
): CreditProvider | null => {
  const provider = CREDIT_REGISTRY[id];
  if (!provider) {
    console.error(
      `Unsupported credit provider: ${id}. Supported providers: ${Object.keys(
        CREDIT_REGISTRY
      ).join(", ")}`
    );
    return null;
  }
  return provider;
};

/**
 * Get all available credit providers
 * @returns Array of all credit provider configurations
 */
export const getAllCreditProviders = (): CreditProvider[] => {
  return Object.values(CREDIT_REGISTRY);
};

/**
 * Get credit provider by store key
 * @param storeKey - Store key to lookup
 * @returns Credit provider configuration or undefined
 */
export const getCreditProviderByStoreKey = (
  storeKey: string
): CreditProvider | undefined => {
  return Object.values(CREDIT_REGISTRY).find((p) => p.storeKey === storeKey);
};

/**
 * Get credit provider by provider key (UI key)
 * @param providerKey - Provider key to lookup
 * @returns Credit provider configuration or undefined
 */
export const getCreditProviderByProviderKey = (
  providerKey: string
): CreditProvider | undefined => {
  return Object.values(CREDIT_REGISTRY).find(
    (p) => p.providerKey === providerKey
  );
};

/**
 * Map API credit type to store key
 * @param apiCreditType - API credit type (aave-v3, euler, hypurrFi)
 * @returns Store key (aave, euler, hypurrFi) or null if provider not found
 */
export const mapApiTypeToStoreKey = (
  apiCreditType: CreditProviderId
): string | null => {
  const provider = getCreditProvider(apiCreditType);
  return provider ? provider.storeKey : null;
};

/**
 * Map store key to API credit type
 * @param storeKey - Store key (aave, euler, hypurrFi)
 * @returns API credit type (aave-v3, euler, hypurrFi)
 */
export const mapStoreKeyToApiType = (
  storeKey: string
): CreditProviderId | undefined => {
  const provider = getCreditProviderByStoreKey(storeKey);
  return provider ? (provider.id as CreditProviderId) : undefined;
};

/**
 * Get provider configuration for API calls
 * @param creditType - Credit provider ID
 * @returns Provider configuration object or null if provider not found
 */
export const getProviderConfig = (
  creditType: CreditProviderId
): CreditProvider["config"] | null => {
  const provider = getCreditProvider(creditType);
  return provider ? provider.config : null;
};

/**
 * Get provider decimals for formatting
 * @param creditType - Credit provider ID
 * @returns Number of decimals for the provider or null if provider not found
 */
export const getProviderDecimals = (
  creditType: CreditProviderId
): number | null => {
  const provider = getCreditProvider(creditType);
  return provider ? provider.decimals : null;
};

/**
 * Check if a provider is supported on a specific chain
 * @param creditType - Credit provider ID
 * @param chainId - Chain ID to check
 * @returns Boolean indicating if provider is supported on chain
 */
export const isProviderSupportedOnChain = (
  creditType: CreditProviderId,
  chainId: number
): boolean => {
  const provider = getCreditProvider(creditType);
  return provider ? provider.chainId === chainId : false;
};

/**
 * Get all supported chain IDs
 * @returns Array of unique chain IDs
 */
export const getSupportedChainIds = (): number[] => {
  const chainIds = Object.values(CREDIT_REGISTRY).map((p) => p.chainId);
  return [...new Set(chainIds)];
};

/**
 * Get credit providers by type classification
 * @param creditType - Credit type classification (spend or borrow)
 * @param chainId - Optional chain ID to filter by
 * @returns Array of credit provider configurations
 */
export const getCreditProvidersByType = (
  creditType: CreditTypeClassification,
  chainId?: number
): CreditProvider[] => {
  let providers = Object.values(CREDIT_REGISTRY).filter(
    (p) => p.creditType === creditType
  );

  if (chainId) {
    providers = providers.filter((p) => p.chainId === chainId);
  }

  return providers;
};

/**
 * Check if a credit provider is of a specific type
 * @param creditProviderId - Credit provider ID
 * @param creditType - Credit type classification to check
 * @returns Boolean indicating if provider matches the type
 */
export const isCreditProviderOfType = (
  creditProviderId: CreditProviderId,
  creditType: CreditTypeClassification
): boolean => {
  const provider = getCreditProvider(creditProviderId);
  return provider ? provider.creditType === creditType : false;
};

/**
 * Get the canDCA status for a particular credit provider.
 * @param creditProviderId - The ID of the credit provider.
 * @returns A boolean indicating if the provider supports DCA.
 * @throws Error if the credit provider is not supported.
 */
export const getCanDCAForProvider = (
  creditProviderId: CreditProviderId
): boolean => {
  const provider = getCreditProvider(creditProviderId);
  return provider?.canDCA ?? false;
};
